// Report Calculations Utility
// All calculation logic for reports: commissions, taxes, profits, metrics

import { Appointment } from '../services/appointmentService';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Client } from '../services/clientDataService';
import { parseYmd } from '../shared/dateUtils';

// ============================================================================
// COMMISSION CALCULATIONS
// ============================================================================

// calculateCommission() removed (Jose 2026-09-01) — superseded by commissionForBarber() in
// shared/growth.ts (the shared brain: per-barber flat rate vs shop sliding scale). Zero callers.

/**
 * Calculate total barber pay (commission + tips + bonuses)
 */
export function calculateBarberPay(params: {
  serviceCommission: number;
  productCommission: number;
  tips: number;
  bonuses?: number;
  emergencyFeeSplit?: number;
}): number {
  const { serviceCommission, productCommission, tips, bonuses = 0, emergencyFeeSplit = 0 } = params;

  return serviceCommission + productCommission + tips + bonuses + emergencyFeeSplit;
}

// ============================================================================
// TAX CALCULATIONS
// ============================================================================

/**
 * Calculate tax amount
 */
export function calculateTax(params: {
  subtotal: number;
  taxRate: number; // Percentage (e.g., 8.5 for 8.5%)
}): {
  taxAmount: number;
  total: number;
} {
  const { subtotal, taxRate } = params;

  const taxAmount = (subtotal * taxRate) / 100;
  const total = subtotal + taxAmount;

  return {
    taxAmount,
    total,
  };
}

// ============================================================================
// REVENUE CALCULATIONS
// ============================================================================

/**
 * Calculate total revenue from appointments
 * Supports both old (status: 'completed') and new (appointmentStatus: 'checked_out') systems
 */
export function calculateTotalRevenue(appointments: Appointment[]): {
  serviceRevenue: number;
  tips: number;
  emergencyFees: number;
  totalRevenue: number;
} {
  let serviceRevenue = 0;
  let tips = 0;
  let emergencyFees = 0;

  appointments.forEach((apt) => {
    // Check both old and new appointment status systems
    const isCompleted =
      apt.status === 'completed' ||
      (apt as any).appointmentStatus === 'checked_out';

    if (isCompleted) {
      serviceRevenue += apt.price || 0;

      // 💰 REAL TIPS: Use tipAmount from Firestore if available
      tips += (apt as any).tipAmount || 0;

      // Emergency fees from appointment if available
      emergencyFees += (apt as any).emergencyFee || 0;
    }
  });

  const totalRevenue = serviceRevenue + tips + emergencyFees;

  return {
    serviceRevenue,
    tips,
    emergencyFees,
    totalRevenue,
  };
}

/**
 * Calculate average ticket price
 */
export function calculateAverageTicket(totalRevenue: number, appointmentCount: number): number {
  if (appointmentCount === 0) return 0;
  return totalRevenue / appointmentCount;
}

/**
 * Calculate revenue per hour
 */
export function calculateRevenuePerHour(totalRevenue: number, totalHoursWorked: number): number {
  if (totalHoursWorked === 0) return 0;
  return totalRevenue / totalHoursWorked;
}

// ============================================================================
// PERFORMANCE METRICS
// ============================================================================

/**
 * Calculate no-show rate
 */
export function calculateNoShowRate(noShows: number, totalAppointments: number): number {
  if (totalAppointments === 0) return 0;
  return (noShows / totalAppointments) * 100;
}

/**
 * Calculate retention rate
 */
export function calculateRetentionRate(returningClients: number, totalClients: number): number {
  if (totalClients === 0) return 0;
  return (returningClients / totalClients) * 100;
}

/**
 * Calculate product upsell rate
 */
export function calculateProductUpsellRate(
  appointmentsWithProducts: number,
  totalAppointments: number
): number {
  if (totalAppointments === 0) return 0;
  return (appointmentsWithProducts / totalAppointments) * 100;
}

/**
 * Calculate on-time rate
 */
export function calculateOnTimeRate(onTimeAppointments: number, totalAppointments: number): number {
  if (totalAppointments === 0) return 0;
  return (onTimeAppointments / totalAppointments) * 100;
}

/**
 * Calculate utilization rate (booked hours / available hours)
 */
export function calculateUtilizationRate(bookedHours: number, availableHours: number): number {
  if (availableHours === 0) return 0;
  return (bookedHours / availableHours) * 100;
}

// ============================================================================
// CLIENT METRICS
// ============================================================================

/**
 * Calculate Client Performance Index (CPI)
 */
export function calculateCPI(params: {
  totalVisits: number;
  totalSpent: number;
  lastVisitDate: Date;
  referralCount: number;
  noShowCount: number;
  cancellationCount: number;
  isModelClient: boolean;
  manualAdjustment?: number;
}): number {
  const {
    totalVisits,
    totalSpent,
    lastVisitDate,
    referralCount,
    noShowCount,
    cancellationCount,
    isModelClient,
    manualAdjustment = 0,
  } = params;

  // Visit score (max 100, 5 points per visit)
  const visitScore = Math.min(totalVisits * 5, 100);

  // Spend score (max 100, 1 point per $10 spent)
  const spendScore = Math.min((totalSpent / 10) * 1, 100);

  // Retention score (max 100, based on days since last visit)
  const daysSinceLastVisit = Math.floor(
    (new Date().getTime() - lastVisitDate.getTime()) / (1000 * 60 * 60 * 24)
  );
  const retentionScore = Math.max(100 - daysSinceLastVisit * 0.5, 0);

  // Referral score (20 points per referral, max 100)
  const referralScore = Math.min(referralCount * 20, 100);

  // Base CPI (average of all scores)
  let baseCPI = (visitScore + spendScore + retentionScore + referralScore) / 4;

  // Model client bonus (+10 points)
  if (isModelClient) {
    baseCPI += 10;
  }

  // Reliability penalty
  const reliabilityPenalty = noShowCount * 5 + cancellationCount * 2;
  baseCPI -= reliabilityPenalty;

  // Apply manual adjustment
  baseCPI += manualAdjustment;

  // Clamp between 0 and 100
  return Math.max(0, Math.min(100, baseCPI));
}

/**
 * Calculate client lifetime value (LTV)
 */
export function calculateClientLTV(params: {
  totalSpent: number;
  totalVisits: number;
  averageVisitsPerYear?: number;
  projectedYears?: number;
}): {
  currentLTV: number;
  projectedLTV: number;
  averagePerVisit: number;
} {
  const { totalSpent, totalVisits, averageVisitsPerYear = 12, projectedYears = 5 } = params;

  const averagePerVisit = totalVisits > 0 ? totalSpent / totalVisits : 0;
  const currentLTV = totalSpent;
  const projectedLTV = averagePerVisit * averageVisitsPerYear * projectedYears;

  return {
    currentLTV,
    projectedLTV,
    averagePerVisit,
  };
}

/**
 * Calculate visit frequency (average days between visits)
 */
export function calculateVisitFrequency(appointments: Appointment[]): number {
  if (appointments.length < 2) return 0;

  // Sort appointments by date
  // parseYmd = LOCAL midnight. A bare `new Date("2026-10-15")` is UTC midnight = prev evening
  // in ET, which can mis-order visits and skew the days-between math. (Jose 2026-10-23)
  const sortedAppts = [...appointments].sort(
    (a, b) => parseYmd(a.date).getTime() - parseYmd(b.date).getTime()
  );

  // Calculate days between each visit
  let totalDays = 0;
  for (let i = 1; i < sortedAppts.length; i++) {
    const prevDate = parseYmd(sortedAppts[i - 1].date);
    const currDate = parseYmd(sortedAppts[i].date);
    const daysBetween = (currDate.getTime() - prevDate.getTime()) / (1000 * 60 * 60 * 24);
    totalDays += daysBetween;
  }

  // Return average
  return totalDays / (sortedAppts.length - 1);
}

// ============================================================================
// COMPARISON CALCULATIONS
// ============================================================================

/**
 * Calculate growth percentage between two periods
 */
export function calculateGrowth(currentValue: number, previousValue: number): number {
  if (previousValue === 0) {
    return currentValue > 0 ? 100 : 0;
  }
  return ((currentValue - previousValue) / previousValue) * 100;
}

/**
 * Calculate variance from target
 */
export function calculateVariance(actual: number, target: number): {
  variance: number;
  percentageOfTarget: number;
  overOrUnder: 'over' | 'under' | 'on-target';
} {
  const variance = actual - target;
  const percentageOfTarget = target > 0 ? (actual / target) * 100 : 0;

  let overOrUnder: 'over' | 'under' | 'on-target' = 'on-target';
  if (actual > target) overOrUnder = 'over';
  if (actual < target) overOrUnder = 'under';

  return {
    variance,
    percentageOfTarget,
    overOrUnder,
  };
}

// ============================================================================
// PROFIT CALCULATIONS
// ============================================================================

/**
 * Calculate shop profit after commissions
 */
export function calculateShopProfit(params: {
  totalRevenue: number;
  totalCommissionsPaid: number;
  productCosts?: number;
  operatingCosts?: number;
}): {
  grossProfit: number;
  netProfit: number;
  profitMargin: number;
} {
  const { totalRevenue, totalCommissionsPaid, productCosts = 0, operatingCosts = 0 } = params;

  const grossProfit = totalRevenue - totalCommissionsPaid;
  const netProfit = grossProfit - productCosts - operatingCosts;
  const profitMargin = totalRevenue > 0 ? (netProfit / totalRevenue) * 100 : 0;

  return {
    grossProfit,
    netProfit,
    profitMargin,
  };
}

/**
 * Calculate product profit margin
 */
export function calculateProductMargin(params: {
  retailPrice: number;
  costPrice: number;
}): {
  profit: number;
  margin: number;
} {
  const { retailPrice, costPrice } = params;

  const profit = retailPrice - costPrice;
  const margin = retailPrice > 0 ? (profit / retailPrice) * 100 : 0;

  return {
    profit,
    margin,
  };
}

// ============================================================================
// DATE/TIME CALCULATIONS
// ============================================================================

/**
 * Get date range for a specific period
 */
export function getDateRangeForPeriod(params: {
  period: 'today' | 'yesterday' | 'this-week' | 'last-week' | 'this-month' | 'last-month' | 'this-year';
  weekStartDay?: 0 | 1 | 2 | 3 | 4 | 5 | 6; // 0=Sunday, 1=Monday, etc.
}): {
  startDate: Date;
  endDate: Date;
} {
  const { period, weekStartDay = 1 } = params; // Default to Monday
  const now = new Date();
  let startDate: Date;
  let endDate: Date;

  switch (period) {
    case 'today':
      startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
      endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
      break;

    case 'yesterday':
      startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 0, 0, 0);
      endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 23, 59, 59);
      break;

    case 'this-week': {
      const currentDay = now.getDay();
      const daysFromWeekStart = (currentDay - weekStartDay + 7) % 7;
      startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysFromWeekStart, 0, 0, 0);
      endDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + 6, 23, 59, 59);
      break;
    }

    case 'last-week': {
      const currentDay = now.getDay();
      const daysFromWeekStart = (currentDay - weekStartDay + 7) % 7;
      startDate = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() - daysFromWeekStart - 7,
        0,
        0,
        0
      );
      endDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate() + 6, 23, 59, 59);
      break;
    }

    case 'this-month':
      startDate = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0);
      endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
      break;

    case 'last-month':
      startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0);
      endDate = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
      break;

    case 'this-year':
      startDate = new Date(now.getFullYear(), 0, 1, 0, 0, 0);
      endDate = new Date(now.getFullYear(), 11, 31, 23, 59, 59);
      break;

    default:
      startDate = now;
      endDate = now;
  }

  return { startDate, endDate };
}

/**
 * Format currency (for display)
 */
export function formatCurrency(amount: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount);
}

/**
 * Format percentage (for display)
 */
export function formatPercentage(value: number, decimals: number = 1): string {
  return `${value.toFixed(decimals)}%`;
}

/**
 * Round to 2 decimal places
 */
export function roundToTwo(num: number): number {
  return Math.round((num + Number.EPSILON) * 100) / 100;
}

// ============================================================================
// GOAL TRACKING & AI INSIGHTS (Phase C)
// ============================================================================

/**
 * Calculate goal progress percentage and status
 */
export function calculateGoalProgress(actual: number, goal: number): {
  percentage: number;
  status: 'on-track' | 'behind' | 'ahead' | 'goal-met';
  message: string;
} {
  if (goal === 0) {
    return {
      percentage: 0,
      status: 'on-track',
      message: 'No goal set'
    };
  }

  const percentage = (actual / goal) * 100;

  let status: 'on-track' | 'behind' | 'ahead' | 'goal-met';
  let message: string;

  if (percentage >= 100) {
    status = 'goal-met';
    message = `🎉 Goal achieved! ${percentage.toFixed(0)}% complete`;
  } else if (percentage >= 90) {
    status = 'ahead';
    message = `🔥 Almost there! ${percentage.toFixed(0)}% of goal`;
  } else if (percentage >= 70) {
    status = 'on-track';
    message = `✅ On track: ${percentage.toFixed(0)}% of goal`;
  } else {
    status = 'behind';
    message = `⚠️ Behind: Only ${percentage.toFixed(0)}% of goal`;
  }

  return { percentage, status, message };
}

/**
 * Generate AI insights based on performance data
 * Returns coaching tips, recommendations, and warnings
 */
export function generateAIInsights(data: {
  revenue: number;
  revenueGoal: number;
  appointments: number;
  productSales: number;
  productSalesGoal: number;
  tipPercentage: number;
  tipGoal: number;
  serviceSpeed: number; // average minutes
  currentTime: Date;
  noShows: number;
  rebookRate: number;
}): {
  tips: string[];
  recommendations: string[];
  warnings: string[];
} {
  const tips: string[] = [];
  const recommendations: string[] = [];
  const warnings: string[] = [];

  const { percentage: revenueProgress } = calculateGoalProgress(data.revenue, data.revenueGoal);
  const currentHour = data.currentTime.getHours();

  // ========== REVENUE CHECKS ==========

  // Mid-day revenue check (3 PM)
  if (currentHour >= 15 && revenueProgress < 50) {
    warnings.push('Revenue is below 50% at 3 PM - consider double-booking or walk-ins');
  }

  // End of day revenue check (5 PM+)
  if (currentHour >= 17) {
    if (revenueProgress >= 100) {
      tips.push('🎉 Daily revenue goal met! Great work!');
    } else if (revenueProgress >= 90) {
      tips.push(`💪 Just $${(data.revenueGoal - data.revenue).toFixed(2)} away from goal!`);
    } else if (revenueProgress < 70) {
      warnings.push('Revenue goal likely won\'t be met today - plan better tomorrow');
    }
  }

  // ========== PRODUCT UPSELL CHECKS ==========

  const productAttachmentRate = data.appointments > 0 ? (data.productSales / data.appointments) * 100 : 0;

  if (productAttachmentRate < 30) {
    tips.push('💰 Focus on product upsells - try beard oil or pomade recommendations');
  }

  if (data.productSales < data.productSalesGoal && currentHour >= 14) {
    recommendations.push(`Need ${data.productSalesGoal - data.productSales} more product sales to hit goal`);
  }

  // ========== SERVICE SPEED CHECKS ==========

  // Ideal speed is 35-40 minutes
  if (data.serviceSpeed > 45) {
    recommendations.push('⚡ Service speed is slow - aim for 35-40 min average to see more clients');
  } else if (data.serviceSpeed < 30) {
    warnings.push('⏱️ Service speed very fast - make sure quality isn\'t suffering');
  } else if (data.serviceSpeed >= 35 && data.serviceSpeed <= 40) {
    tips.push('✅ Perfect service speed! Keep it up!');
  }

  // ========== TIP COACHING ==========

  if (data.tipPercentage < data.tipGoal) {
    const tipDiff = data.tipGoal - data.tipPercentage;
    tips.push(`💡 Tip rate ${tipDiff.toFixed(1)}% below goal - suggest premium add-ons (hot towel, beard grooming)`);
  } else if (data.tipPercentage >= data.tipGoal + 5) {
    tips.push('🌟 Excellent tip rate! Clients love your service!');
  }

  // ========== NO-SHOW MANAGEMENT ==========

  if (data.noShows > 0) {
    recommendations.push(`📱 ${data.noShows} no-show${data.noShows > 1 ? 's' : ''} today - send reminder texts for tomorrow's clients`);
  }

  // ========== REBOOKING RATE ==========

  if (data.rebookRate < 60) {
    recommendations.push('💬 Low rebooking rate - ask clients to schedule next visit at checkout');
  } else if (data.rebookRate >= 80) {
    tips.push('🎯 Excellent rebooking rate! Great client retention!');
  }

  // ========== TIME-BASED MOTIVATIONAL TIPS ==========

  if (currentHour < 12) {
    tips.push('☀️ Great start to the day! Stay focused on goals!');
  } else if (currentHour >= 12 && currentHour < 15 && revenueProgress >= 50) {
    tips.push('🔥 Halfway through the day and on track! Keep up the momentum!');
  }

  return { tips, recommendations, warnings };
}

/**
 * Determine best metric from daily performance
 * Returns the standout achievement
 */
export function determineBestMetric(data: {
  serviceSpeed: number;
  tipPercentage: number;
  productSales: number;
  revenue: number;
  appointments: number;
  rebookRate: number;
}): string {
  const achievements: { metric: string; score: number }[] = [];

  // Service speed (ideal 35-40 min, score higher if closer)
  if (data.serviceSpeed >= 30 && data.serviceSpeed <= 40) {
    const speedScore = 100 - Math.abs(37.5 - data.serviceSpeed) * 5;
    achievements.push({ metric: `Fastest service time: ${data.serviceSpeed.toFixed(0)} min avg`, score: speedScore });
  }

  // Tip percentage (higher is better)
  if (data.tipPercentage >= 15) {
    achievements.push({ metric: `Great tips: ${data.tipPercentage.toFixed(1)}% average`, score: data.tipPercentage * 2 });
  }

  // Product sales
  if (data.productSales >= 3) {
    achievements.push({ metric: `Sold ${data.productSales} products`, score: data.productSales * 15 });
  }

  // Revenue
  if (data.revenue >= 400) {
    achievements.push({ metric: `High revenue: $${data.revenue.toFixed(2)}`, score: (data.revenue / 10) });
  }

  // Appointments completed
  if (data.appointments >= 8) {
    achievements.push({ metric: `Completed ${data.appointments} appointments`, score: data.appointments * 8 });
  }

  // Rebooking rate
  if (data.rebookRate >= 70) {
    achievements.push({ metric: `${data.rebookRate.toFixed(0)}% rebooking rate`, score: data.rebookRate });
  }

  // Return highest scoring achievement
  if (achievements.length === 0) {
    return 'Completed another day of great work!';
  }

  achievements.sort((a, b) => b.score - a.score);
  return achievements[0].metric;
}
